/**
 * Lock Service - Per-question locking for Pool Assignments
 *
 * Manages question-level locks for the pool assignment system.
 * Locks are stored directly in student documents as Q##_lock fields.
 * Uses Firestore transactions for atomic operations.
 * No real-time listeners - uses timestamp-based expiration.
 */

import { doc, updateDoc, getDoc, Timestamp, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import { QuestionLock } from '../types';

// Lock durations
const LOCK_DURATION_MS = 2 * 60 * 60 * 1000; // 2 hours
const ACTIVITY_EXTENSION_MS = 30 * 60 * 1000; // Extend by 30 minutes on activity

/**
 * Result of a lock acquisition attempt
 */
export interface LockAcquisitionResult {
  success: boolean;
  reason?: string;
  currentLock?: QuestionLock;
  extended?: boolean;
  acquired?: boolean;
}

/**
 * Result of a lock check
 */
export interface LockCheckResult {
  isLocked: boolean;
  lock?: QuestionLock;
  isOwnLock?: boolean;
}

/**
 * Attempt to acquire lock on a student's question
 * Uses Firestore transaction for atomic operation
 *
 * @param collectionName - The scanned data collection name (e.g., 'buraxilis2')
 * @param studentDocId - The student document ID
 * @param questionNumber - The question number (e.g., 'Q79')
 * @param teacherId - The teacher's ID
 * @param teacherName - The teacher's display name
 * @returns Lock acquisition result
 */
export const acquireLock = async (
  collectionName: string,
  studentDocId: string,
  questionNumber: string,
  teacherId: string,
  teacherName: string
): Promise<LockAcquisitionResult> => {
  const lockField = `${questionNumber}_lock`;
  const studentRef = doc(db, collectionName, studentDocId);

  try {
    const result = await runTransaction(db, async (transaction) => {
      const studentDoc = await transaction.get(studentRef);

      if (!studentDoc.exists()) {
        throw new Error('Tələbə sənədi tapılmadı');
      }

      const data = studentDoc.data();
      const existingLock = data[lockField] as QuestionLock | undefined;

      // Check if lock exists and is still valid
      if (existingLock && existingLock.expiresAt) {
        const now = Date.now();
        const expiresAt = existingLock.expiresAt.toMillis ? existingLock.expiresAt.toMillis() : existingLock.expiresAt;

        if (now < expiresAt) {
          // Lock is still valid
          if (existingLock.teacherId === teacherId) {
            // Same teacher - extend lock
            const newExpiresAt = Timestamp.fromMillis(now + LOCK_DURATION_MS);
            transaction.update(studentRef, {
              [`${lockField}.expiresAt`]: newExpiresAt,
              [`${lockField}.lastActivity`]: Timestamp.now()
            });
            return { success: true, extended: true };
          } else {
            // Different teacher - lock conflict
            return {
              success: false,
              reason: `Sual ${existingLock.teacherName} tərəfindən yoxlanılır`,
              currentLock: existingLock
            };
          }
        }
        // Lock expired - continue to acquire
      }

      // Acquire new lock
      const now = Timestamp.now();
      const expiresAt = Timestamp.fromMillis(Date.now() + LOCK_DURATION_MS);

      const newLock: QuestionLock = {
        teacherId,
        teacherName,
        lockedAt: now,
        expiresAt,
        lastActivity: now
      };

      transaction.update(studentRef, {
        [lockField]: newLock
      });

      return { success: true, acquired: true };
    });

    console.log(`🔒 Lock ${result.acquired ? 'acquired' : (result.extended ? 'extended' : 'failed')}: ${questionNumber} for student ${studentDocId}`);
    return result;
  } catch (error) {
    console.error('❌ Lock acquisition error:', error);
    return { success: false, reason: 'Kilit əldə edilə bilmədi' };
  }
};

/**
 * Release lock manually (when teacher closes question or saves grade)
 *
 * @param collectionName - The scanned data collection name
 * @param studentDocId - The student document ID
 * @param questionNumber - The question number
 * @param teacherId - The teacher's ID (must own the lock to release it)
 * @returns true if lock was released, false otherwise
 */
export const releaseLock = async (
  collectionName: string,
  studentDocId: string,
  questionNumber: string,
  teacherId: string
): Promise<boolean> => {
  const lockField = `${questionNumber}_lock`;
  const studentRef = doc(db, collectionName, studentDocId);

  try {
    const result = await runTransaction(db, async (transaction) => {
      const studentDoc = await transaction.get(studentRef);

      if (!studentDoc.exists()) {
        return false;
      }

      const data = studentDoc.data();
      const existingLock = data[lockField] as QuestionLock | undefined;

      // Only release if this teacher owns the lock
      if (existingLock && existingLock.teacherId === teacherId) {
        // Use deleteField equivalent - set to null for Firestore
        transaction.update(studentRef, {
          [lockField]: null
        });
        return true;
      }

      return false;
    });

    if (result) {
      console.log(`🔓 Lock released: ${questionNumber} for student ${studentDocId}`);
    }
    return result;
  } catch (error) {
    console.error('❌ Lock release error:', error);
    return false;
  }
};

/**
 * Extend lock on activity (typing, clicking, etc.)
 * Called periodically while teacher is actively working
 *
 * @param collectionName - The scanned data collection name
 * @param studentDocId - The student document ID
 * @param questionNumber - The question number
 * @param teacherId - The teacher's ID
 * @returns true if lock was extended, false otherwise
 */
export const extendLock = async (
  collectionName: string,
  studentDocId: string,
  questionNumber: string,
  teacherId: string
): Promise<boolean> => {
  const lockField = `${questionNumber}_lock`;
  const studentRef = doc(db, collectionName, studentDocId);

  try {
    const studentDoc = await getDoc(studentRef);

    if (!studentDoc.exists()) {
      return false;
    }

    const data = studentDoc.data();
    const existingLock = data[lockField] as QuestionLock | undefined;

    // Only extend if this teacher owns the lock
    if (existingLock && existingLock.teacherId === teacherId) {
      const now = Date.now();
      const newExpiresAt = Timestamp.fromMillis(now + ACTIVITY_EXTENSION_MS);

      await updateDoc(studentRef, {
        [`${lockField}.expiresAt`]: newExpiresAt,
        [`${lockField}.lastActivity`]: Timestamp.now()
      });

      console.log(`🔄 Lock extended: ${questionNumber} for student ${studentDocId}`);
      return true;
    }

    return false;
  } catch (error) {
    console.error('❌ Lock extension error:', error);
    return false;
  }
};

/**
 * Check if question is locked by another teacher
 *
 * @param collectionName - The scanned data collection name
 * @param studentDocId - The student document ID
 * @param questionNumber - The question number
 * @param currentTeacherId - The current teacher's ID
 * @returns Lock check result with lock info if locked
 */
export const checkLock = async (
  collectionName: string,
  studentDocId: string,
  questionNumber: string,
  currentTeacherId: string
): Promise<LockCheckResult> => {
  const lockField = `${questionNumber}_lock`;
  const studentRef = doc(db, collectionName, studentDocId);

  try {
    const studentDoc = await getDoc(studentRef);

    if (!studentDoc.exists()) {
      return { isLocked: false };
    }

    const data = studentDoc.data();
    const existingLock = data[lockField] as QuestionLock | undefined;

    if (!existingLock || !existingLock.expiresAt) {
      return { isLocked: false };
    }

    // Check if lock is expired
    const now = Date.now();
    const expiresAt = existingLock.expiresAt.toMillis ? existingLock.expiresAt.toMillis() : existingLock.expiresAt;

    if (now >= expiresAt) {
      // Lock expired
      return { isLocked: false };
    }

    // Lock is valid
    const isOwnLock = existingLock.teacherId === currentTeacherId;

    return {
      isLocked: !isOwnLock,
      lock: existingLock,
      isOwnLock
    };
  } catch (error) {
    console.error('❌ Lock check error:', error);
    return { isLocked: false };
  }
};

/**
 * Check locks for a student's specific question from already-loaded data
 * This is a synchronous check using data already in memory
 *
 * @param studentData - The student data object
 * @param questionNumber - The question number to check
 * @param currentTeacherId - The current teacher's ID
 * @returns Lock check result
 */
export const checkLockFromData = (
  studentData: any,
  questionNumber: string,
  currentTeacherId: string
): LockCheckResult => {
  const lockField = `${questionNumber}_lock`;
  const existingLock = studentData[lockField] as QuestionLock | undefined;

  if (!existingLock || !existingLock.expiresAt) {
    return { isLocked: false };
  }

  // Check if lock is expired
  const now = Date.now();
  const expiresAt = existingLock.expiresAt.toMillis ? existingLock.expiresAt.toMillis() : existingLock.expiresAt;

  if (now >= expiresAt) {
    return { isLocked: false };
  }

  const isOwnLock = existingLock.teacherId === currentTeacherId;

  return {
    isLocked: !isOwnLock,
    lock: existingLock,
    isOwnLock
  };
};

/**
 * Release all locks owned by a teacher
 * Used when a teacher leaves or is frozen
 *
 * @param collectionName - The scanned data collection name
 * @param teacherId - The teacher's ID
 * @param studentDocIds - Array of student document IDs to check
 * @param questionNumbers - Array of question numbers to check
 * @returns Number of locks released
 */
export const releaseAllTeacherLocks = async (
  collectionName: string,
  teacherId: string,
  studentDocIds: string[],
  questionNumbers: string[]
): Promise<number> => {
  let releasedCount = 0;

  for (const studentDocId of studentDocIds) {
    for (const questionNumber of questionNumbers) {
      const released = await releaseLock(collectionName, studentDocId, questionNumber, teacherId);
      if (released) {
        releasedCount++;
      }
    }
  }

  console.log(`🔓 Released ${releasedCount} locks for teacher ${teacherId}`);
  return releasedCount;
};

/**
 * Get lock expiration constants (for UI display)
 */
export const getLockDurations = () => ({
  lockDurationMs: LOCK_DURATION_MS,
  lockDurationMinutes: LOCK_DURATION_MS / (60 * 1000),
  activityExtensionMs: ACTIVITY_EXTENSION_MS,
  activityExtensionMinutes: ACTIVITY_EXTENSION_MS / (60 * 1000)
});
